import React, { useRef, useState } from "react";
import api, { BACKEND_URL } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Upload, Link2, X } from "lucide-react";
import { toast } from "sonner";

/**
 * PublicImageUploadInput — same toggle UX as ImageUploadInput but uses the
 * unauthenticated /api/uploads/public-image endpoint. For use on public sign-up
 * forms (doctor enrol, distributor register, etc.).
 *
 * Tighter limits than the staff version: 3 MB max, images only (png/jpg/webp).
 */
export default function PublicImageUploadInput({ label, value, onChange, testId, aspect = "1/1", fallbackText, hint }) {
    const [busy, setBusy] = useState(false);
    const [mode, setMode] = useState("upload");
    const fileRef = useRef(null);

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 3 * 1024 * 1024) return toast.error("Image must be under 3 MB");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads/public-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const fullUrl = `${BACKEND_URL}${data.url}`;
            onChange(fullUrl);
            toast.success("Photo uploaded");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const aspectStyle = { aspectRatio: aspect.replace("/", " / ") };
    const previewSize = 96;

    return (
        <div data-testid={testId}>
            {label && <Label className="text-xs uppercase tracking-widest text-stone-500">{label}</Label>}

            <div className="flex gap-1 mt-1 mb-2">
                <button
                    type="button"
                    onClick={() => setMode("upload")}
                    className={`text-[10px] uppercase tracking-widest px-3 py-1 border ${mode === "upload" ? "bg-sky-700 text-stone-50 border-sky-700" : "border-stone-300 text-stone-600"}`}
                    data-testid={testId ? `${testId}-mode-upload` : undefined}
                >
                    <Upload className="w-3 h-3 inline mr-1" />Upload photo
                </button>
                <button
                    type="button"
                    onClick={() => setMode("url")}
                    className={`text-[10px] uppercase tracking-widest px-3 py-1 border ${mode === "url" ? "bg-sky-700 text-stone-50 border-sky-700" : "border-stone-300 text-stone-600"}`}
                    data-testid={testId ? `${testId}-mode-url` : undefined}
                >
                    <Link2 className="w-3 h-3 inline mr-1" />Paste URL
                </button>
            </div>

            {mode === "url" ? (
                <Input
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="https://… (link to your photo)"
                    className="rounded-none border-stone-300"
                    data-testid={testId ? `${testId}-url` : undefined}
                />
            ) : (
                <div className="flex gap-2">
                    <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleFile} className="hidden" data-testid={testId ? `${testId}-file` : undefined} />
                    <Button type="button" onClick={() => fileRef.current?.click()} disabled={busy} variant="outline" className="rounded-none border-stone-400 flex-1 justify-start" data-testid={testId ? `${testId}-upload-btn` : undefined}>
                        <Upload className="w-4 h-4 mr-2" />{busy ? "Uploading…" : "Choose photo (≤ 3 MB)"}
                    </Button>
                    {value && (
                        <Button type="button" onClick={() => onChange("")} variant="ghost" className="rounded-none" data-testid={testId ? `${testId}-clear` : undefined}>
                            <X className="w-4 h-4" />
                        </Button>
                    )}
                </div>
            )}

            <div className="mt-3 flex items-start gap-3">
                <div className="border border-stone-200 bg-stone-50 overflow-hidden shrink-0" style={{ width: previewSize, ...aspectStyle }}>
                    {value ? (
                        <img src={value} alt="preview" className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full grid place-items-center text-stone-400 text-xs">{fallbackText || "preview"}</div>
                    )}
                </div>
                <div className="flex-1">
                    {value && <p className="text-[10px] text-stone-500 break-all max-w-md">{value}</p>}
                    {hint && <p className="text-xs text-stone-500 mt-1">{hint}</p>}
                    {!hint && <p className="text-xs text-stone-500 mt-1">PNG / JPG / WebP, max 3 MB</p>}
                </div>
            </div>
        </div>
    );
}
